import { PlusCircleOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  AuthType,
  EncodingType,
  Integration,
  IntegrationMethod,
  IntegrationMutableProperties,
  IntegrationProtocol,
} from "../../../api";
import { useIntegrationStateContext } from "../context/IntegrationStateContext";

interface CreateIntegrationButtonProps {
  consumerId: string;
  integrations: Integration[];
}

export function CreateIntegrationButton({
  consumerId,
  integrations,
}: CreateIntegrationButtonProps) {
  const { t } = useTranslation();

  const {
    createIntegrationState,
    createIntegration,
    setCreateIntegrationState,
    refreshIntegrations,
    setActiveCell,
  } = useIntegrationStateContext();

  const [createdIntegrationId, setCreatedIntegrationId] = useState<string>("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  /*
    Need to find the newly created integration in the table and set it as active, then scroll to it.
    New integration can be created at any index witin the array, so we need to find it by its ID.
  */
  useEffect(() => {
    if (!createdIntegrationId) return;
    const newIntegrationIndex = integrations?.findIndex(
      (integration: Integration) => integration.id === createdIntegrationId
    );

    if (newIntegrationIndex !== -1 && integrations) {
      setActiveCell({
        row: newIntegrationIndex,
        col: "id",
        data: integrations[newIntegrationIndex],
      });
      scrollToRow(createdIntegrationId);

      setCreatedIntegrationId("");
    }
  }, [createdIntegrationId, integrations, setActiveCell]);

  const onCreateIntegration = async () => {
    const newRecord: IntegrationMutableProperties = {
      name: t("integrations:new-integration"),
      consumerId: consumerId,
      protocol: IntegrationProtocol.HTTP,
      endpoint: "",
      method: IntegrationMethod.POST,
      auth: {
        authType: AuthType.Basic,
        authValue: "",
        encoding: EncodingType.BASE64,
      },
      events: [],
    };

    setCreateIntegrationState({ loading: true, error: false });
    const result = await createIntegration(newRecord);
    setCreateIntegrationState({ loading: false, error: result.error });

    if (result.error) {
      setIsModalOpen(true);
    } else {
      setCreatedIntegrationId(result.response.data.id);
      refreshIntegrations();
    }
  };

  return (
    <div data-testid="create-integration-button">
      <Button
        onClick={onCreateIntegration}
        loading={createIntegrationState.loading}
        type="primary"
        aria-label="create-integration-button"
        style={{ width: "200px", height: "40px" }}
      >
        <PlusCircleOutlined />
        {t("integrations:new-integration")}
      </Button>
      <Modal
        title={t("error")}
        open={isModalOpen}
        onOk={() => setIsModalOpen(false)}
        onCancel={() => setIsModalOpen(false)}
      >
        <p style={{ color: "#EC5B56" }}>
          <strong>{t("error-create")}</strong>
        </p>
      </Modal>
    </div>
  );
}

const scrollToRow = (key: string) => {
  if (!key) return;

  const row = document.querySelector(`.ant-table-row[data-row-key="${key}"]`);
  if (row) {
    row.scrollIntoView({ behavior: "smooth", block: "center" });
  }
};
